import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * UnifiedAllergies — dashboard-embedded allergy editor.
 *
 * Replaces the generic ModifyPreferencesScreen for the Allergies tab.
 * Allergy-specific copy, safety-tone styling (red/amber), quick-add chips
 * for the top-12 allergens, and a 4-level severity picker per entry
 * (Mild / Moderate / Severe / Life-threatening).
 *
 * Data shape stays compatible with the legacy endpoint: each entry saved
 * as { ingredient, level } where level maps from severity. Loads via
 * dataService.loadUserPreferences(userId, 'allergy'), saves via
 * dataService.saveUserPreferences(userId, list, 'allergy').
 */

const EASE = [0.22, 1, 0.36, 1];

const COMMON_ALLERGENS = [
  { label: 'Peanut',      emoji: 'Pe' },
  { label: 'Tree nuts',   emoji: 'TN' },
  { label: 'Milk',        emoji: 'Mi' },
  { label: 'Egg',         emoji: 'Eg' },
  { label: 'Wheat',       emoji: 'Wh' },
  { label: 'Gluten',      emoji: 'Gl' },
  { label: 'Soy',         emoji: 'So' },
  { label: 'Fish',        emoji: 'Fi' },
  { label: 'Shellfish',   emoji: 'Sh' },
  { label: 'Sesame',      emoji: 'Se' },
  { label: 'Mustard',     emoji: 'Mu' },
  { label: 'Sulfites',    emoji: 'Su' },
];

const SEVERITY_LEVELS = [
  { id: 'mild',             label: 'Mild',             sub: 'Mild discomfort',        level: 0.1, tone: 'amber' },
  { id: 'moderate',         label: 'Moderate',         sub: 'Clear reaction',         level: 0.4, tone: 'orange' },
  { id: 'severe',           label: 'Severe',           sub: 'Serious reaction',       level: 0.7, tone: 'red' },
  { id: 'life_threatening', label: 'Life-threatening', sub: 'Anaphylaxis / EpiPen',   level: 1.0, tone: 'critical' },
];

const SEVERITY_TONES = {
  amber:    { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  orange:   { bg: '#FFE8D6',                fg: '#8A4A00',               solid: '#EA580C' },
  red:      { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  critical: { bg: '#1F1314',                fg: '#FFE8E8',               solid: '#7F1D1D' },
};

function levelToSeverity(level) {
  if (level == null) return 'moderate';
  if (level >= 0.85) return 'life_threatening';
  if (level >= 0.55) return 'severe';
  if (level >= 0.25) return 'moderate';
  return 'mild';
}

export default function UnifiedAllergies({ userId }) {
  const [items, setItems] = useState([]); // [{ ingredient, level, severity }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [customInput, setCustomInput] = useState('');

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.loadUserPreferences(userId, 'allergy');
        if (cancelled) return;
        const normalized = (Array.isArray(data) ? data : []).map((x) => ({
          ingredient: x.ingredient || x.name || String(x),
          level: typeof x.level === 'number' ? x.level : 0.5,
          severity: levelToSeverity(x.level),
        }));
        setItems(normalized);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const has = (name) => items.some((i) => i.ingredient.toLowerCase() === name.toLowerCase());

  const add = (name, severity = 'moderate') => {
    if (!name.trim()) return;
    if (has(name)) { flash('red', `${name} is already in your list.`); return; }
    const level = SEVERITY_LEVELS.find((s) => s.id === severity)?.level ?? 0.4;
    setItems((prev) => [...prev, { ingredient: name.trim(), level, severity }]);
  };

  const updateSeverity = (ingredient, severity) => {
    const level = SEVERITY_LEVELS.find((s) => s.id === severity)?.level ?? 0.4;
    setItems((prev) => prev.map((i) => i.ingredient === ingredient ? { ...i, level, severity } : i));
  };

  const remove = (ingredient) => setItems((prev) => prev.filter((i) => i.ingredient !== ingredient));

  const save = async () => {
    if (!userId) { flash('red', 'Sign in required.'); return; }
    setSaving(true);
    try {
      const payload = items.map(({ ingredient, level }) => ({ ingredient, level }));
      await dataService.saveUserPreferences(userId, payload, 'allergy');
      flash('green', 'Allergies saved. Chat will use these on every menu.');
    } catch (e) {
      flash('red', 'Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  const severeCount = items.filter((i) => i.severity === 'severe' || i.severity === 'life_threatening').length;

  return (
    <div style={{ padding: '18px 20px' }}>

      {/* Alert */}
      <AnimatePresence>
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 14,
              background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
              color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
              fontSize: 13, fontWeight: 800,
            }}
          >{alert.text}</motion.div>
        )}
      </AnimatePresence>

      {/* Safety callout */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{
          position: 'relative', overflow: 'hidden',
          padding: '18px 20px',
          borderRadius: 20,
          background: 'linear-gradient(160deg, #FFE9E9 0%, #FFD6D6 100%)',
          border: '1.5px solid #F4B6B6',
          marginBottom: 22,
          display: 'flex', alignItems: 'center', gap: 14,
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: '#fff', color: 'var(--cwm-red-text)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
          boxShadow: '0 6px 14px rgba(185,28,28,0.18)',
        }}>
          <ShieldIcon size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-red-text)' }}>
            SAFETY FIRST
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--cwm-ink)', marginTop: 2, lineHeight: 1.4 }}>
            {items.length === 0
              ? 'Add your allergies so chat can flag unsafe menu items automatically.'
              : severeCount > 0
                ? `Tracking ${items.length} allergen${items.length === 1 ? '' : 's'} — ${severeCount} marked severe or life-threatening.`
                : `Tracking ${items.length} allergen${items.length === 1 ? '' : 's'}.`}
          </div>
        </div>
      </motion.div>

      {/* Quick add */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
        style={{ marginBottom: 20 }}
      >
        <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>QUICK ADD COMMON ALLERGENS</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {COMMON_ALLERGENS.map((a, i) => {
            const already = has(a.label);
            return (
              <motion.button
                key={a.label} type="button"
                onClick={() => !already && add(a.label, 'moderate')}
                disabled={already}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28, delay: 0.1 + i * 0.02 }}
                whileHover={!already ? { y: -2 } : {}} whileTap={!already ? { scale: 0.96 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px 8px 8px', borderRadius: 999,
                  background: already ? 'var(--cwm-gray-100)' : '#fff',
                  color: already ? 'var(--cwm-gray-400)' : 'var(--cwm-red-text)',
                  border: `1.5px solid ${already ? 'var(--cwm-gray-200)' : '#F4B6B6'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  cursor: already ? 'not-allowed' : 'pointer',
                  boxShadow: already ? 'none' : '0 4px 10px rgba(185,28,28,0.1)',
                  transition: 'all 160ms ease',
                }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: already ? 'var(--cwm-gray-200)' : 'var(--cwm-red-light)',
                  color: already ? 'var(--cwm-gray-500)' : 'var(--cwm-red-text)',
                  display: 'grid', placeItems: 'center',
                  fontSize: 9.5, fontWeight: 900, letterSpacing: '-0.05em',
                }}>{already ? <CheckIcon size={11} /> : a.emoji}</span>
                {a.label}
              </motion.button>
            );
          })}
        </div>
      </motion.div>

      {/* Custom add */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.16, ease: EASE }}
        style={{ marginBottom: 24, display: 'flex', gap: 8, alignItems: 'stretch' }}
      >
        <input
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(customInput, 'moderate'); setCustomInput(''); } }}
          placeholder="Type another allergen (e.g. celery, lupin, mango)…"
          style={{
            flex: 1, minWidth: 0,
            padding: '12px 16px', borderRadius: 14,
            border: '1.5px solid #F4B6B6',
            background: 'var(--cwm-red-light)',
            color: 'var(--cwm-ink)',
            fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => { add(customInput, 'moderate'); setCustomInput(''); }}
          disabled={!customInput.trim()}
          style={{
            padding: '12px 18px', borderRadius: 14,
            background: customInput.trim() ? '#B91C1C' : 'var(--cwm-gray-100)',
            color: customInput.trim() ? '#fff' : 'var(--cwm-gray-500)',
            border: 'none',
            fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
            cursor: customInput.trim() ? 'pointer' : 'not-allowed',
            boxShadow: customInput.trim() ? '0 4px 12px rgba(185,28,28,0.28)' : 'none',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
          <PlusIcon size={14} /> Add
        </button>
      </motion.div>

      {/* List */}
      {loading ? (
        <LoadingBlock />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ marginBottom: 24 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>YOUR ALLERGIES ({items.length})</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <AnimatePresence initial={false}>
              {items.map((item, i) => (
                <AllergyRow
                  key={item.ingredient}
                  item={item}
                  index={i}
                  onSeverityChange={(sev) => updateSeverity(item.ingredient, sev)}
                  onRemove={() => remove(item.ingredient)}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingTop: 14, borderTop: '1px solid var(--cwm-gray-100)' }}>
        <div style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
          Changes apply to every chat + recommendation immediately after save.
        </div>
        <button
          type="button" onClick={save} disabled={saving}
          className="cwm-btn"
          style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? <Dots /> : <><SaveIcon size={14} /> Save allergies</>}
        </button>
      </div>
    </div>
  );
}

function AllergyRow({ item, index, onSeverityChange, onRemove }) {
  const currentSev = SEVERITY_LEVELS.find((s) => s.id === item.severity) || SEVERITY_LEVELS[1];
  const tone = SEVERITY_TONES[currentSev.tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      transition={{ duration: 0.28, delay: index * 0.03, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: '14px 16px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 12,
            background: tone.bg, color: tone.fg,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            border: `1.5px solid ${tone.solid}33`,
          }}>
            <ShieldIcon size={16} />
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {item.ingredient}
          </div>
        </div>
        <button
          type="button" onClick={onRemove} aria-label="remove"
          style={{
            width: 30, height: 30, borderRadius: 999,
            background: '#fff', color: 'var(--cwm-gray-500)',
            border: '1px solid var(--cwm-gray-200)',
            cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
          <TrashIcon size={13} />
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {SEVERITY_LEVELS.map((s) => {
          const on = s.id === item.severity;
          const st = SEVERITY_TONES[s.tone];
          return (
            <button
              key={s.id} type="button"
              onClick={() => onSeverityChange(s.id)}
              style={{
                padding: '6px 12px', borderRadius: 999,
                background: on ? st.solid : st.bg,
                color: on ? '#fff' : st.fg,
                border: `1.5px solid ${on ? st.solid : 'transparent'}`,
                fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 900,
                cursor: 'pointer',
                boxShadow: on ? `0 4px 10px ${st.solid}55` : 'none',
                transition: 'all 140ms ease',
              }}>
              {s.label}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div style={{
      padding: '36px 24px', textAlign: 'center',
      borderRadius: 20,
      background: 'var(--cwm-gray-50)',
      border: '1.5px dashed var(--cwm-gray-300)',
      marginBottom: 24,
    }}>
      <div style={{
        width: 56, height: 56, margin: '0 auto 12px',
        borderRadius: '50%',
        background: '#fff', color: 'var(--cwm-gray-500)',
        display: 'grid', placeItems: 'center',
        border: '1.5px dashed var(--cwm-gray-300)',
      }}>
        <ShieldIcon size={24} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>
        No allergies tracked yet
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-500)', maxWidth: 360, margin: '0 auto' }}>
        Tap a quick-add chip or type one below. Chat will use your allergies on every menu, every time.
      </div>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--cwm-gray-500)', fontSize: 13, fontWeight: 700 }}>
      <div style={{ width: 32, height: 32, margin: '0 auto 10px', border: '3px solid var(--cwm-red-light)', borderTopColor: 'var(--cwm-red-text)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      Loading your allergies…
    </div>
  );
}

function Dots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}

// icons
function ShieldIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/><path d="M12 9v4M12 16h.01"/></svg>; }
function PlusIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function CheckIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function TrashIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function SaveIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8"/></svg>; }
